// import React from 'react';
import React, { useState, useEffect, useGlobal } from "reactn";
import styled from "styled-components";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import Notification from "shared-anpost-components/components/notification";
import BasketSummary from "./BasketSummary/BasketSummary";
import MyDeliveriesSummary from "../../_core/MyDeliveriesSummary/MyDeliveriesSummary";
import NotificationBox from "../../_common/notificationBox/NotificationBox";
import MyDeliveriesHooks from "../../../hooks/myDeliveriesHooks";
import AppHook from "../../../hooks/appHook";
import { getContentFromContentApi, validateCnPBasket } from "../../../services/Api";
import { getCookie } from "../../../helpers/Helpers";

const Overview = ({ userProfile, history, openTab }) => {
    const { useWidgetsScripts, useWidgetsStyles } = AppHook();
    const [showBusinessBanner, setShowBusinessBanner] = useGlobal("businessBanner");
    const [isCustomerRegistered] = useGlobal("mydelsRegistered");
    const [isBusiness, setIsBusiness] = useGlobal("isBusiness");
    const [myAccountContent, setMyAccountContent] = useState(null);
    const [container, setContainer] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const { showMobileBanner, showCustomsBanner, callMobileDataApi } = MyDeliveriesHooks({ userProfile });
    const { phones } = userProfile;
    const baseUrl = window.location.origin;

    const closeBusinessBanner = () => {
        setShowBusinessBanner(false);
        localStorage.setItem("businessTabOpenCount", "0");
    };

    // check if cart basket has invalid items
    const validateBasket = async () => {
        const basketReference = getCookie("ClickPostBasketReference");
        if (!basketReference) return;
        const CIAMCustomerID = getCookie("CIAMCustomerID");
        const sessionId = window.cmsSessionId;
        const response = await validateCnPBasket({ CIAMCustomerID, sessionId, basketReference });
        const basketData = response ? response.data : "";

        if (basketData && (basketData.validationErrors || basketData.validationErrors.length > 0)) {
            basketData.validationErrors.forEach(basketError => {
                if (basketError.MailServiceType !== "MailsLabelService") {
                    setShowWarning(true);
                }
            });
        }

        if (basketData && (!basketData.validationErrors || basketData.validationErrors.length === 0 || basketData.isBasketValid)) {
            showWarning && setShowWarning(false);
        }
    };
    useWidgetsStyles(myAccountContent?.css);
    useWidgetsScripts(myAccountContent?.js);

    const OverviewHeading = styled.div`
        h3 {
            font-size: 2.2rem;
            @media only screen and (min-width: ${props => props.theme.tablet}) {
                font-size: 2.6rem;
            }
        }

        p {
            margin-bottom: 2.4rem;
            @media only screen and (min-width: ${props => props.theme.tablet}) {
                margin-bottom: 3.2rem;
            }
        }
    `;

    useEffect(() => {
        if (isBusiness && !userProfile?.profile?.business?.name && openTab === "overview") {
            const businessTabOpenCount = localStorage.getItem("businessTabOpenCount");

            // if business banner was closed and it hasn't been the 7th time user opens the business overview tab - increase open count
            // else show banner and remove local storage variable
            if (businessTabOpenCount && +businessTabOpenCount < 7) {
                localStorage.setItem("businessTabOpenCount", `${+businessTabOpenCount + 1}`);
                setShowBusinessBanner(false);
            } else {
                setShowBusinessBanner(true);
                localStorage.removeItem("businessTabOpenCount");
            }
        }

        // send data layer if business if available and banner is showing
        if (showBusinessBanner) {
            window.dataLayer.push({
                event: "banner",
                banner_name: "Business"
            });
        }
    }, [showBusinessBanner]);

    useEffect(() => {
        const myAccountContentApi = JSON.parse(sessionStorage.getItem("myAccountContentApi"));
        if (myAccountContentApi) {
            setMyAccountContent(myAccountContentApi);
        } else {
            getContentFromContentApi()
                .then(res => {
                    if (res.status === 200) {
                        if (res.data) {
                            const contentData = res.data.data;
                            const widgetsCss = contentData.widgetCss.replace(/\\"/g, '"');
                            const widgetsJs = contentData.widgetJs.replace(/\\"/g, '"');
                            const personalContent = contentData.content.filter(el => el.key === "overview.personal")[0].html;
                            const businessContent = contentData.content.filter(el => el.key === "overview.business")[0].html;
                            const contentHtml = {
                                personal: personalContent.replace(/\\"/g, '"'),
                                business: businessContent.replace(/\\"/g, '"'),
                                css: widgetsCss,
                                js: widgetsJs
                            };
                            setMyAccountContent(contentHtml);
                            sessionStorage.setItem("myAccountContentApi", JSON.stringify(contentHtml));
                        }
                    }
                })
                .catch(err => {
                    setMyAccountContent({});
                    console.log(err);
                });
        }
    }, []);

    useEffect(() => {
        const basketSummaryContainer = document.querySelector(".m124 .m124__container");
        if (basketSummaryContainer) {
            setContainer(basketSummaryContainer);
        }
    }, [myAccountContent, isBusiness]);

    // send the datalayer if the return label is expired and banner is showing
    useEffect(() => {
        if (showWarning) {
            window.dataLayer.push({
                event: "banner",
                banner_name: "Return label"
            });
        }
    }, [showWarning]);

    // send data layer if the customs banner is showin
    useEffect(() => {
        if (showCustomsBanner) {
            window.dataLayer.push({
                event: "banner",
                banner_name: "Customs Charges"
            });
        }
    }, [showCustomsBanner]);

    // send data layer if the mobile not verified banner is showing
    useEffect(() => {
        if (showMobileBanner) {
            window.dataLayer.push({
                event: "banner",
                banner_name: "Phone number"
            });
        }
    }, [showMobileBanner]);

    useEffect(() => {
        if (isCustomerRegistered) {
            callMobileDataApi();
        }
    }, [isCustomerRegistered]);

    useEffect(() => {
        validateBasket();
    }, []);

    return (
        <>
            <OverviewHeading>
                <h3 className="h4">Overview</h3>
                <p>Conveniently manage your post and parcels.</p>
            </OverviewHeading>
            {showMobileBanner && (
                <Notification
                    colour="blue"
                    text="Please add and verify your mobile phone number"
                    additionalText="We use your mobile number to identify your parcels so we can include them here."
                    ctaText={`${phones.length === 0 ? "Add" : "Update"}`}
                    withMargin
                    ctaClick={e => {
                        e.preventDefault();
                        window.location.href = `${baseUrl}/myAccount/My-deliveries?return=My-account`;
                    }}
                />
            )}
            {showCustomsBanner && (
                <Notification
                    colour="blue"
                    text="You have one or more parcels with unpaid customs charges awaiting delivery"
                    className="gtm-banner"
                    ctaText="View"
                    withMargin
                    ctaClick={e => {
                        e.preventDefault();
                        window.location.href = `${baseUrl}/myAccount/My-deliveries`;
                    }}
                />
            )}
            {showWarning && (
                <Notification
                    colour="yellow"
                    text="Return label has expired"
                    additionalText="Please remove it from you basket and add another one"
                    ctaText="View basket"
                    withMargin
                    ctaClick={e => {
                        e.preventDefault();
                        window.location.href = `${baseUrl}/Post-Parcels/Self-Service/Basket`;
                    }}
                ></Notification>
            )}
            {showBusinessBanner && (
                <NotificationBox
                    heading="Are you a business?"
                    text="Add your business to your profile to access business services and discounts."
                    borderColor="#003D4C"
                    onClose={closeBusinessBanner}
                    ctaText="Add your business"
                    ctaClick={() => history.push("/Profile/edit-business")}
                />
            )}
            {Object.keys(userProfile).length > 0 && <MyDeliveriesSummary userProfile={userProfile} />}
            {isBusiness ? <div dangerouslySetInnerHTML={{ __html: myAccountContent?.business }}></div> : <div dangerouslySetInnerHTML={{ __html: myAccountContent?.personal }}></div>}
            {container ? createPortal(<BasketSummary containerNode={container} />, container) : null}
        </>
    );
};

export default Overview;

Overview.propTypes = {
    userProfile: PropTypes.object,
    history: PropTypes.object,
    openTab: PropTypes.string
};
